import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { format } from 'date-fns';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  ChangeField,
  getFieldLabel,
  getFieldNullLabel,
  getReasonOptionsForField,
  TaskFieldChangeReason,
  TreatmentCheckInCheckbox,
} from '@/constants/task-field-change-reasons';
import { cn } from '@/lib/utils';

/** Minimal task info for bulk update selection */
export interface BulkUpdateTask {
  id: string;
  name: string;
  matter: string;
  due_date?: string | null;
}

/** Selected task info for bulk update */
export interface SelectedTaskInfo {
  id: string;
  name: string;
}

interface FieldChangeReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    reason: TaskFieldChangeReason;
    reasonDetail?: string;
    selectedTaskIds?: string[];
    /** Full task info for selected tasks (for push changes) */
    selectedTasks?: SelectedTaskInfo[];
  }) => Promise<void>;
  fieldType: ChangeField;
  oldValue: string | null;
  newValue: string;
  taskName: string;
  /** Optional list of other tasks that can also be updated (single mode only) */
  otherTasks?: BulkUpdateTask[];
  /** Modal size - use 'md' for bulk update scenarios with task list */
  size?: 'sm' | 'md';
  /** Mode: 'single' for task detail, 'bulk' for bulk toolbar actions */
  mode?: 'single' | 'bulk';
  /** Number of tasks being updated (bulk mode only) */
  taskCount?: number;
  /** Optional recent task update text to suggest as pre-fill */
  recentUpdate?: string;
  /** ISO timestamp of the recent update (for freshness check) */
  recentUpdateCreatedAt?: string;
  /** Optional warning message displayed as an amber banner */
  warningMessage?: string;
  /** Optional required checkboxes that must all be checked before confirming */
  requiredCheckboxes?: readonly TreatmentCheckInCheckbox[];
}

/**
 * Format value for display based on field type.
 * Handles date formatting and null value display.
 */
const formatValueForDisplay = (
  field: ChangeField,
  value: string | null,
): string => {
  if (value === null) {
    return getFieldNullLabel(field);
  }

  // Format dates, pass through other values
  if (field === 'due_date') {
    try {
      return format(new Date(value), 'MMM d, yyyy');
    } catch {
      return value;
    }
  }

  return value;
};

/**
 * Modal for capturing the reason when a user changes a tracked task field.
 *
 * This modal appears when a user modifies fields like status, due date, or assignee.
 * It captures the reason for the change which is then stored as a correlated
 * TaskUpdate comment linked to the domain event via correlation_id.
 *
 * Features:
 * - Auto-selects reason when only one option available
 * - Enter key submits the form
 * - Prevents closing while submitting
 * - Shows old → new value summary
 */
export const FieldChangeReasonModal: React.FC<FieldChangeReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  fieldType,
  oldValue,
  newValue,
  taskName,
  otherTasks = [],
  size = 'sm',
  mode = 'single',
  taskCount,
  recentUpdate,
  recentUpdateCreatedAt,
  warningMessage,
  requiredCheckboxes = [],
}) => {
  const isBulkMode = mode === 'bulk';
  // Memoize reason options to avoid unnecessary effect triggers
  const reasonOptions = React.useMemo(
    () => getReasonOptionsForField(fieldType),
    [fieldType],
  );
  const hasOnlyOneOption = reasonOptions.length === 1;
  const defaultReason = hasOnlyOneOption ? reasonOptions[0].value : null;

  // Initialize reason to first option if there's only one
  const [reason, setReason] = React.useState<TaskFieldChangeReason | null>(
    () => defaultReason,
  );
  const [reasonDetail, setReasonDetail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = React.useState<Set<string>>(
    new Set(),
  );
  const [suggestionDismissed, setSuggestionDismissed] = React.useState(false);
  const [hasInteracted, setHasInteracted] = React.useState(false);
  const [checkedBoxes, setCheckedBoxes] = React.useState<Set<string>>(
    new Set(),
  );

  const isOtherReason = reason === 'other';
  const allCheckboxesChecked =
    requiredCheckboxes.length === 0 ||
    requiredCheckboxes.every((cb) => checkedBoxes.has(cb.id));
  const isDetailRequired = isOtherReason;
  const canSubmit =
    reason !== null &&
    (!isDetailRequired || reasonDetail.trim() !== '') &&
    allCheckboxesChecked;

  // Format values for display
  const oldValueFormatted = formatValueForDisplay(fieldType, oldValue);
  const newValueFormatted = formatValueForDisplay(fieldType, newValue);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setReason(defaultReason);
      setReasonDetail('');
      setIsSubmitting(false);
      setSelectedTaskIds(new Set());
      setSuggestionDismissed(false);
      setHasInteracted(false);
      setCheckedBoxes(new Set());
    }
  }, [isOpen, defaultReason]);

  const toggleTaskSelection = (taskId: string) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const toggleAllTasks = () => {
    if (selectedTaskIds.size === otherTasks.length) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(otherTasks.map((t) => t.id)));
    }
  };

  const handleConfirm = async () => {
    if (!canSubmit || !reason) return;

    setIsSubmitting(true);
    try {
      const selectedTaskIdArray =
        selectedTaskIds.size > 0 ? Array.from(selectedTaskIds) : undefined;

      // Build selectedTasks with full info for push changes
      const selectedTasksInfo: SelectedTaskInfo[] | undefined =
        selectedTaskIdArray && otherTasks
          ? otherTasks
              .filter((t) => selectedTaskIdArray.includes(t.id))
              .map((t) => ({ id: t.id, name: t.name }))
          : undefined;

      await onConfirm({
        reason,
        reasonDetail: reasonDetail.trim() || undefined,
        selectedTaskIds: selectedTaskIdArray,
        selectedTasks: selectedTasksInfo,
      });
    } catch (error) {
      console.error('Failed to confirm field change reason:', error);
      toast.error('Failed to save change reason. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Handle Enter to submit
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSubmit && !isSubmitting) {
      e.preventDefault();
      handleConfirm();
    }
  };

  // Recent update suggestion — check freshness at render time to avoid stale memoized values
  const isRecentEnough =
    recentUpdateCreatedAt &&
    Date.now() - new Date(recentUpdateCreatedAt).getTime() < 5 * 60 * 1000;
  const showSuggestion =
    recentUpdate && isRecentEnough && !suggestionDismissed && !hasInteracted;

  const handleUseSuggestion = () => {
    if (recentUpdate) {
      setReasonDetail(recentUpdate);
      setSuggestionDismissed(true);
    }
  };

  // Determine modal width - use larger size when showing bulk update options (single mode only)
  const hasBulkOptions = !isBulkMode && otherTasks.length > 0;
  const modalWidthClass =
    hasBulkOptions || size === 'md' ? 'sm:max-w-md' : 'sm:max-w-sm';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className={modalWidthClass} zIndex={60}>
        <DialogHeader className="pb-0 min-w-0">
          <DialogTitle className="text-base font-medium text-gray-900 dark:text-zinc-100">
            {isBulkMode
              ? `Bulk ${getFieldLabel(fieldType)} Change`
              : `${getFieldLabel(fieldType)} Change`}
          </DialogTitle>
          {!isBulkMode && (
            <p className="text-xs font-medium text-gray-500 dark:text-zinc-400 truncate">
              {taskName}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-3 min-w-0">
          {/* Change summary */}
          {isBulkMode ? (
            <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-zinc-800/50">
              <span className="text-xs text-gray-600 dark:text-zinc-400">
                Updating{' '}
                <span className="font-medium text-gray-900 dark:text-zinc-100">
                  {taskCount} {taskCount === 1 ? 'task' : 'tasks'}
                </span>{' '}
                to{' '}
                <span className="font-medium text-gray-900 dark:text-zinc-100">
                  {newValueFormatted}
                </span>
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-zinc-800/50">
              <span className="text-xs font-medium text-gray-500 dark:text-zinc-400 truncate">
                {oldValueFormatted}
              </span>
              <ArrowRightIcon className="size-3 text-gray-400 dark:text-zinc-500 shrink-0" />
              <span className="text-xs font-medium text-gray-900 dark:text-zinc-100 truncate">
                {newValueFormatted}
              </span>
            </div>
          )}

          {/* Warning banner */}
          {warningMessage && (
            <div className="px-3 py-2 rounded border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                {warningMessage}
              </p>
            </div>
          )}

          {/* Required checkboxes */}
          {requiredCheckboxes.length > 0 && (
            <div className="space-y-2">
              {requiredCheckboxes.map((cb) => {
                const isChecked = checkedBoxes.has(cb.id);
                return (
                  <label
                    key={cb.id}
                    htmlFor={`required-cb-${cb.id}`}
                    className="flex items-start gap-2 cursor-pointer"
                  >
                    <input
                      id={`required-cb-${cb.id}`}
                      type="checkbox"
                      checked={isChecked}
                      onChange={() =>
                        setCheckedBoxes((prev) => {
                          const next = new Set(prev);
                          if (next.has(cb.id)) {
                            next.delete(cb.id);
                          } else {
                            next.add(cb.id);
                          }
                          return next;
                        })
                      }
                      className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 dark:border-zinc-600 text-blue-600 focus:ring-blue-500 dark:bg-zinc-800 cursor-pointer"
                    />
                    <span className="text-xs text-slate-700 dark:text-zinc-300">
                      {cb.label}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Reason selection - hide when there's only one option */}
          {!hasOnlyOneOption && (
            <div className="space-y-1.5">
              <Label
                htmlFor="change-reason"
                className="text-xs font-medium text-gray-700 dark:text-zinc-300"
              >
                Reason
              </Label>
              <Select
                value={reason || undefined}
                onValueChange={(value) =>
                  setReason(value as TaskFieldChangeReason)
                }
              >
                <SelectTrigger id="change-reason" className="h-8 text-xs">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {reasonOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Recent update suggestion */}
          {showSuggestion && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-zinc-800/50 border border-gray-200 dark:border-zinc-700">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-gray-500 dark:text-zinc-400 mb-0.5">
                  From your recent update
                </p>
                <p className="text-xs text-gray-700 dark:text-zinc-300 line-clamp-2">
                  {recentUpdate}
                </p>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <button
                  type="button"
                  onClick={handleUseSuggestion}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 px-2 py-0.5 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                >
                  Use this
                </button>
                <button
                  type="button"
                  onClick={() => setSuggestionDismissed(true)}
                  className="text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 p-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors"
                  aria-label="Dismiss suggestion"
                >
                  <XMarkIcon className="size-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Detail textarea */}
          <div className="space-y-1.5">
            <Label
              htmlFor="change-reason-detail"
              className="text-xs font-medium text-gray-700 dark:text-zinc-300"
            >
              {hasOnlyOneOption ? 'Reason' : 'Details'}
              {isDetailRequired && (
                <span className="text-red-500 ml-0.5">*</span>
              )}
              {!isDetailRequired && requiredCheckboxes.length > 0 && (
                <span className="text-slate-400 ml-1 text-xs font-normal">
                  (optional)
                </span>
              )}
            </Label>
            <Textarea
              id="change-reason-detail"
              value={reasonDetail}
              onChange={(e) => {
                setReasonDetail(e.target.value);
                if (e.target.value.trim()) setHasInteracted(true);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Brief explanation..."
              rows={5}
              className={cn(
                'resize-none text-sm',
                isDetailRequired && !reasonDetail.trim() && reason !== null
                  ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                  : '',
              )}
              autoFocus
            />
          </div>

          {/* Other tasks to update - only show in single mode if there are other tasks */}
          {!isBulkMode && otherTasks.length > 0 && (
            <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-zinc-700">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700 dark:text-zinc-300">
                  Also update these tasks
                </span>
                <button
                  type="button"
                  onClick={toggleAllTasks}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors"
                >
                  {selectedTaskIds.size === otherTasks.length
                    ? 'Deselect all'
                    : 'Select all'}
                </button>
              </div>
              <div className="max-h-52 overflow-y-auto space-y-0.5">
                {otherTasks.map((task) => {
                  const isSelected = selectedTaskIds.has(task.id);
                  const checkboxId = `bulk-task-${task.id}`;
                  return (
                    <label
                      key={task.id}
                      htmlFor={checkboxId}
                      className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors',
                        isSelected
                          ? 'bg-gray-100 dark:bg-zinc-800'
                          : 'hover:bg-gray-50 dark:hover:bg-zinc-800/50',
                      )}
                    >
                      <input
                        id={checkboxId}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleTaskSelection(task.id)}
                        className="h-3.5 w-3.5 rounded border-gray-300 dark:border-zinc-600 text-gray-900 focus:ring-gray-500 dark:bg-zinc-800 cursor-pointer"
                      />
                      <span
                        className={cn(
                          'flex-1 min-w-0 text-xs truncate',
                          isSelected
                            ? 'text-gray-900 dark:text-zinc-100 font-medium'
                            : 'text-gray-600 dark:text-zinc-400',
                        )}
                      >
                        {task.name}
                      </span>
                      <span className="text-xs font-medium text-gray-500 dark:text-zinc-500 shrink-0 tabular-nums">
                        {task.due_date
                          ? formatValueForDisplay('due_date', task.due_date)
                          : 'No date'}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
